import { Component } from '@angular/core';
import { ContactCreateGQL, ContactOrigin, CreateContactInput } from "../../../../shared/graphql/generated/graphql";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { InputTextareaModule } from "primeng/inputtextarea";
import { ButtonModule } from "primeng/button";
import { RippleModule } from "primeng/ripple";
import { MessageModule } from "primeng/message";
import { CommonModule } from "@angular/common";
import { InputTextModule } from "primeng/inputtext";
import { TooltipModule } from "primeng/tooltip";
import { FormErrorComponent } from "../../../../shared/components/form-error/form-error.component";
import { RequestIdComponent } from "../../../../shared/components/request-id/request-id.component";
import { reportErrorForSupport } from "../../../../shared/sentry/error-reporting";
import { Store } from "@ngrx/store";
import { selectPlayerSelf } from "../../../player-self/store/player-self.selectors";
import { distinctUntilChanged, map } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: 'cs-contact-page',
  standalone: true,
  imports: [
    FormErrorComponent,
    InputTextareaModule,
    ReactiveFormsModule,
    ButtonModule,
    RippleModule,
    MessageModule,
    CommonModule,
    InputTextModule,
    TooltipModule,
    RequestIdComponent
  ],
  templateUrl: './contact-page.component.html',
  styleUrl: './contact-page.component.scss'
})
export class ContactPageComponent {
  contactForm = this.fb.group({
    name: [''],
    email: ['', [Validators.required, Validators.email]],
    phone: [''],
    subject: ['', [Validators.required]],
    publicId: [''],
    message: ['', [Validators.required]],
  });

  isSubmitting = false;
  showSuccess = false;
  showError = false;

  /**
   * Support reference for a failed submission. The message beside it is a fixed sentence, so
   * without this a report of "the contact form does not work" has nothing to look up.
   */
  errorReference: string | null = null;

  private currentPublicId = '';

  readonly csIdTooltip = 'Your CS ID can be found in the top right corner when logged in, or in your profile in the CircleSquare app.';

  constructor(private contactCreateGQL: ContactCreateGQL, private fb: FormBuilder, private store: Store) {
    this.store.select(selectPlayerSelf).pipe(
      map(player => player?.publicId ?? ''),
      distinctUntilChanged(),
      takeUntilDestroyed(),
    ).subscribe(publicId => {
      this.currentPublicId = publicId;
      this.contactForm.patchValue({ publicId });
    });
  }

  onSubmit() {

    const payload: CreateContactInput = {
      name: this.contactForm.value.name,
      email: this.contactForm.value.email!,
      phone: this.contactForm.value.phone,
      message: this.contactForm.value.message,
      origin: ContactOrigin.CirclesquareWebsite,
      topic: this.contactForm.value.subject!,
      publicId: this.contactForm.value.publicId || undefined,
    }

    this.showSuccess = false;
    this.showError = false;
    this.errorReference = null;
    this.isSubmitting = true;
    this.contactCreateGQL.mutate({data: payload}).subscribe({
      next: () => {
        this.showSuccess = true;
        this.contactForm.reset();
        this.contactForm.patchValue({ publicId: this.currentPublicId });
        this.isSubmitting = false;
      },
      error: (err) => {
        this.showError = true;
        this.errorReference = reportErrorForSupport(err, { tags: { surface: 'contact-form' } });
        this.isSubmitting = false;
      }
    })
  }
}